import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Download } from "lucide-react";
import { HERO_STATES, BRAND } from "@/data/content";
import { attachBlobVideo } from "@/lib/blobVideo";

export default function Hero() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const targetTime = useRef(0);
  const smoothTime = useRef(0);
  const rafRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [exitY, setExitY] = useState(0);
  const [reduced, setReduced] = useState(false);

  // Load the video fully into memory (blob URL) so scroll scrubbing works in
  // Chrome even on hosts (Cloudflare Pages) that don't support HTTP range requests.
  useEffect(() => {
    let url;
    let cancelled = false;
    attachBlobVideo(videoRef.current, [
      { src: "/hero.webm", type: "video/webm" },
      { src: "/hero.mp4", type: "video/mp4" },
    ]).then((u) => {
      url = u;
      if (cancelled && u) URL.revokeObjectURL(u);
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  // detect reduced-motion preference
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const set = () => setReduced(mq.matches);
    set();
    mq.addEventListener("change", set);
    return () => mq.removeEventListener("change", set);
  }, []);

  // map scroll position -> progress [0,1]
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      // parallax: once the hero is fully scrubbed and starts exiting, let the
      // last frame drift slower than scroll so the next section slides over it.
      const past = -rect.top - total;
      const ey = past > 0 ? Math.min(past, window.innerHeight) * 0.4 : 0;
      setExitY(ey);
      const v = videoRef.current;
      const d = v && v.duration && isFinite(v.duration) ? v.duration : 0;
      if (d && !reduced) targetTime.current = p * d;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [reduced]);

  // smooth scrub loop — lerp toward target time for buttery seeking
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (reduced) {
      v.muted = true;
      v.loop = true;
      v.play().catch(() => {});
      return;
    }

    let seeking = false;
    const onSeeked = () => {
      seeking = false;
    };
    v.addEventListener("seeked", onSeeked);

    const tick = () => {
      const vid = videoRef.current;
      if (vid && vid.readyState >= 1 && vid.duration && isFinite(vid.duration)) {
        smoothTime.current += (targetTime.current - smoothTime.current) * 0.12;
        if (!seeking && Math.abs(targetTime.current - smoothTime.current) > 0.01) {
          seeking = true;
          try {
            vid.currentTime = smoothTime.current;
          } catch (e) {
            seeking = false;
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      v.removeEventListener("seeked", onSeeked);
    };
  }, [reduced]);

  const n = HERO_STATES.length;
  const pos = progress * (n - 1); // 0 .. n-1, each state centered on an integer
  const activeIndex = Math.min(n - 1, Math.max(0, Math.round(pos)));
  const showScrollCue = progress < 0.04;

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative z-0"
      style={{ height: "400vh" }}
    >
      {/* pinned viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden grain">
        <video
          ref={videoRef}
          className="hero-video"
          style={{
            transform: `translateY(${exitY}px) scale(${1 + exitY / 2600})`,
            willChange: "transform",
          }}
          muted
          playsInline
          preload="auto"
          autoPlay={reduced}
          loop={reduced}
          data-testid="hero-video"
        />
        <div className="hero-tint" />
        <div className="hero-vignette" />
        <div className="glow-field" style={{ mixBlendMode: "screen", opacity: 0.7 }} />

        {/* overlay content */}
        <div className="absolute inset-0 z-10">
          <h1 className="sr-only">Jonas Beel — Creative &amp; Communications Strategy Leader: web design, UX/UI, front-end development, branding and digital marketing.</h1>
          <div className="mx-auto max-w-[1400px] h-full px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-20">
            <div className="flex items-end justify-between gap-8">
              {/* left: scroll-linked headline states */}
              <div className="relative max-w-3xl min-h-[280px] sm:min-h-[420px] w-full">
                {HERO_STATES.map((s, i) => {
                  const d = pos - i; // <0 = upcoming (below), >0 = scrolled past (above)
                  const y = -d * 62;
                  // quick fade-in + long plateau so each headline is "there" fast and holds
                  const ad = Math.abs(d);
                  const HOLD = 0.42;
                  const FADE = 0.12;
                  const opacity = Math.max(0, Math.min(1, 1 - (ad - HOLD) / FADE));
                  return (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        opacity,
                        transform: `translateY(${y}px)`,
                        pointerEvents: opacity > 0.5 ? "auto" : "none",
                        willChange: "transform, opacity",
                      }}
                    >
                      <span className="chip inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-6 label !text-[0.62rem] !text-[var(--ink-dim)]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
                        {s.kicker}
                      </span>
                      <h2 className="display text-[3rem] leading-[0.95] sm:text-[5rem] lg:text-[6.5rem] text-[var(--ink)]">
                        {s.line1}{" "}
                        <span className="serif-italic text-[var(--ink)]">{s.accent}</span>
                      </h2>
                      <p className="mt-6 max-w-md text-sm sm:text-base text-[var(--ink-dim)] leading-relaxed">
                        {s.sub}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* right: meta column */}
              <div className="hidden lg:flex flex-col items-end text-right gap-7 pb-2">
                <div>
                  <div className="label mb-2">Discipline</div>
                  <div className="text-sm text-[var(--ink)]">{BRAND.tagline}</div>
                </div>
                <div>
                  <div className="label mb-2">Based</div>
                  <div className="text-sm text-[var(--ink)]">{BRAND.based}</div>
                </div>
                <div>
                  <div className="label mb-2">Open to</div>
                  <div className="text-sm text-[var(--ink)]">New projects & roles</div>
                </div>
              </div>
            </div>

            {/* hero calls to action */}
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#work"
                data-testid="hero-cta-work"
                className="btn-light flex items-center gap-2 rounded-full pl-6 pr-2 py-3 text-sm font-medium"
              >
                View my work
                <span className="grid place-items-center h-8 w-8 rounded-full bg-[#0a0707] text-[var(--ink)]">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-connect"
                className="chip rounded-full px-6 py-3 text-sm text-[var(--ink)] hover:border-[var(--line-strong)] transition-colors"
              >
                Let's connect
              </a>
              <a
                href={BRAND.resume}
                download
                data-testid="hero-cta-resume"
                className="chip rounded-full px-5 py-3 text-sm text-[var(--ink)] hover:border-[var(--line-strong)] transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download resume
              </a>
            </div>

            {/* scrub progress bar — hidden for now (kept for future use)
            <div className="mt-10 flex items-center gap-4">
              <span className="label whitespace-nowrap">
                {String(activeIndex + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
              </span>
              <div className="scrub-bar flex-1 rounded-full">
                <span style={{ transform: `scaleX(${Math.max(0.02, progress)})` }} />
              </div>
              <span className="label whitespace-nowrap">{Math.round(progress * 100)}%</span>
            </div>
            */}
          </div>
        </div>

        {/* scroll cue */}
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 transition-opacity duration-500"
          style={{ opacity: showScrollCue ? 1 : 0 }}
        >
          <div className="label flex items-center gap-2">
            <span>Scroll to play</span>
            <span className="inline-block h-8 w-[1px] bg-[var(--line-strong)] relative overflow-hidden">
              <span
                className="absolute left-0 top-0 w-full bg-[var(--accent)]"
                style={{ height: "40%", animation: "scrollcue 1.6s ease-in-out infinite" }}
              />
            </span>
          </div>
        </div>
      </div>

      <style>{`@keyframes scrollcue{0%{transform:translateY(-100%)}100%{transform:translateY(250%)}}`}</style>
    </section>
  );
}
